import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO — MAINS REHAB (PROGRAMME DELIVERY)
//
// Large-scale mains replacement programme across multiple sites. This is
// about PROGRAMME HEALTH — multiple schemes at different stages being
// orchestrated from above. The drama is a RED site that the AI re-prioritises.
//
// UNIQUE VISUAL DNA:
// 1. WIDEST after capital — city-scale multi-site view (zoom 13.5-14.5)
// 2. 5 sites with pipe-lay happening at 2-3 in PARALLEL (visible from wide)
// 3. multi-site-rag constellation as the PERSISTENT backdrop
// 4. flow-pulse as METERAGE PROGRESS lines (blue arteries extending)
// 5. Budget burndown hud-metric as the tension device
// 6. One site goes RED (contractor underperformance) then AI re-prioritises
// 7. Excavations at multiple sites simultaneously
//
// Not street-level. Not single-scheme. This is programme orchestration.
// ──────────────────────────────────────────────────────────────────────────

// ─── Programme sites (spread across the full city) ──────────────────────
const HUB = infra('control-centre', gp(-2, -4));

// 5 mains renewal schemes at different stages
const SITE_2 = infra('pump-alpha', gp(18, 19));        // Scheme 2: Pipe-laying
const SITE_3 = infra('gas-reg', gp(7, 25));            // Scheme 3: Excavating
const SITE_4 = infra('depot-east', gp(14, -16));       // Scheme 4: RED (problem)

// Pipe-lay paths at active sites
const PIPE_SITE_2: [number, number][] = [
  gp(16, 19), gp(17, 19), gp(18, 19), gp(19, 19), gp(20, 19),
];
const PIPE_SITE_3: [number, number][] = [
  gp(5, 25), gp(6, 25), gp(7, 25), gp(8, 25), gp(9, 25),
];

// Meterage progress flow paths (hub → sites)
const PROGRESS_2: [number, number][] = [gp(-2, -4), gp(6, 6), gp(12, 12), gp(18, 19)];
const PROGRESS_4: [number, number][] = [gp(-2, -4), gp(4, -8), gp(10, -12), gp(14, -16)];

export const mainsRehabProgrammeScenario = defineScenario(
  {
    id: 'mains-rehab-programme',
    title: 'Mains Replacement Programme & Executive Overview Management',
    description: '5 schemes across the city at different stages. Contractor RED site, AI re-prioritisation, programme RAG.',
    hudLabel: 'PROGRAMME DELIVERY',
    telemetry: 'mains-rehab',
    activeInfraIds: ['control-centre', 'pump-beta', 'pump-alpha', 'gas-reg', 'depot-east', 'tower-south'],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: PROGRAMME OVERVIEW — 5 sites, all stages visible (0–25s)
    // ═══════════════════════════════════════════════════════════════════════
    act('Programme Overview', [
      dimStart,

      beat(PACING.ESTABLISH, [
        establishingWide(HUB, { bearing: -15, pitch: 42, zoom: 13.5 }),
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'programme',
          sites: [
            { coordinates: gp(-25, -12), status: 'green' },
            { coordinates: gp(18, 19), status: 'green' },
            { coordinates: gp(7, 25), status: 'amber' },
            { coordinates: gp(14, -16), status: 'green' },
            { coordinates: gp(-18, 28), status: 'green' },
          ],
          linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
        }},
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#0ea5e9', emissive: 0.4 } },
        hudEvent('programme', 'PROGRAMME DELIVERY', '5 mains renewal schemes. 12.4km total meterage. £18M programme budget.', 'info'),
        hudRegion([
          { name: 'Scheme 1 — Station Rd', status: 'complete', properties: 45 },
          { name: 'Scheme 2 — Oak Lane', status: 'pipe-laying', properties: 62 },
          { name: 'Scheme 3 — Park Rd', status: 'excavating', properties: 38 },
          { name: 'Scheme 4 — Kings Way', status: 'active', properties: 55 },
          { name: 'Scheme 5 — River Walk', status: 'mobilising', properties: 40 },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'meterage', kind: 'counter', label: 'Meterage', value: 4200, unit: 'm / 12400m', severity: 'info' } },
        { type: 'hud-metric', duration: 0, params: { id: 'budget-burn', kind: 'burndown', label: 'Budget', value: 7, target: 18, unit: '£M', severity: 'info' } },
      ]),

      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(-2, -4), infraId: 'control-centre', zoom: 14, pitch: 45, bearing: 10 } },
        hudEvent('status', 'PROGRAMME RAG', 'Overall: GREEN. 4 of 5 schemes on track. Scheme 3 amber — material delay.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Programme Confidence', value: 84, unit: '%', severity: 'success' } },
        hudTeam([
          { name: 'Contractor A (Schemes 1-3)', status: 'on-track', destination: '3 schemes' },
          { name: 'Contractor B (Schemes 4-5)', status: 'on-track', destination: '2 schemes' },
        ]),
      ]),

      productScreen('Programme Delivery — Overview', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — programme overview with 5 schemes TBD',
        role: 'Programme Manager',
        url: 'app.skewb.io/programme/delivery',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: PARALLEL PIPE-LAY — Active sites working (25–60s)
    // Camera visits sites 2 and 3 where pipe-lay is happening simultaneously.
    // Meterage ticks up. Excavations visible.
    // ═══════════════════════════════════════════════════════════════════════
    act('Parallel Pipe-Lay', [
      // Excavations at site 3
      { type: 'excavation', duration: 4000, params: { coordinates: gp(6, 25), radius: 12, id: 'exc-3a' } } as ScenarioStep,
      { type: 'excavation', duration: 4000, params: { coordinates: gp(8, 25), radius: 12, id: 'exc-3b' } } as ScenarioStep,

      // Pipe-lay at site 2
      beat(9000, [
        dronePushIn(SITE_2, { bearing: -20, zoom: 16, duration: 9000 }),
        { type: 'pipe-lay', duration: 9000, params: { path: PIPE_SITE_2, color: '#38bdf8', id: 'pipe-2' } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(18, 19), infraId: 'pump-alpha', color: '#10b981', emissive: 0.5 } },
        hudEvent('works', 'SCHEME 2 — PIPE-LAYING', 'Oak Lane: 340m of 580m complete. 63mm MDPE. On schedule.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'meterage', kind: 'counter', label: 'Meterage', value: 4540, unit: 'm / 12400m', severity: 'info' } },
        highlightAt(gp(18, 19), 150),
        highlightAt(gp(16, 19), 120),
        highlightAt(gp(20, 19), 120),
      ]),

      // Pipe-lay at site 3
      beat(8000, [
        dronePushIn(SITE_3, { bearing: 20, zoom: 16, duration: 8000 }),
        { type: 'pipe-lay', duration: 8000, params: { path: PIPE_SITE_3, color: '#38bdf8', id: 'pipe-3' } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(7, 25), infraId: 'gas-reg', color: '#f59e0b', emissive: 0.5 } },
        hudEvent('works', 'SCHEME 3 — EXCAVATING + PIPE', 'Park Rd: 120m of 420m. Material delay resolved. Catching up.', 'warning'),
        { type: 'hud-metric', duration: 0, params: { id: 'meterage', kind: 'counter', label: 'Meterage', value: 4660, unit: 'm / 12400m', severity: 'info' } },
        highlightAt(gp(7, 25), 150),
        highlightAt(gp(5, 25), 120),
      ]),

      // Progress flow lines from hub
      { type: 'flow-pulse', duration: 0, params: { id: 'progress-2', path: PROGRESS_2, color: '#10b981', speed: 0.8 } } as ScenarioStep,

      beat(PACING.BEAT, [
        craneReveal(HUB, { zoom: 14, bearing: -5 }),
        hudTeam([
          { name: 'Scheme 2 — Oak Lane', status: 'on-site', destination: '340m / 580m' },
          { name: 'Scheme 3 — Park Rd', status: 'on-site', destination: '120m / 420m' },
          { name: 'Scheme 4 — Kings Way', status: 'mobilising', destination: 'Crew arriving' },
        ]),
        notification('PARALLEL DELIVERY', '2 schemes pipe-laying simultaneously. Programme meterage accelerating.', 'success', PACING.BEAT),
      ]),

      productScreen('Scheme Health — Active Sites', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — parallel scheme health view TBD',
        role: 'Programme Manager',
        url: 'app.skewb.io/programme/scheme-health',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: RED SITE — Contractor underperformance (60–100s)
    // Site 4 goes RED. Contractor B failing on productivity. Budget burn
    // accelerates. Programme confidence drops. AI flags intervention.
    // ═══════════════════════════════════════════════════════════════════════
    act('Exception — Site 4', [
      // Site 4 goes RED
      { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#ef4444', emissive: 0.8 } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'programme' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'programme',
        sites: [
          { coordinates: gp(-25, -12), status: 'green' },
          { coordinates: gp(18, 19), status: 'green' },
          { coordinates: gp(7, 25), status: 'amber' },
          { coordinates: gp(14, -16), status: 'red' },
          { coordinates: gp(-18, 28), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      // Red risk flow
      { type: 'flow-pulse', duration: 0, params: { id: 'risk-4', path: PROGRESS_4, color: '#ef4444', speed: 1.5 } } as ScenarioStep,
      { type: 'color-grade', duration: 0, params: { tint: '#ef4444', intensity: 0.05, vignette: 0.18 } } as ScenarioStep,

      beat(7000, [
        dronePushIn(SITE_4, { bearing: 30, zoom: 15.5, duration: 7000 }),
        hudEvent('alert', 'RED — KINGS WAY', 'Contractor B: 14m/day vs 28m/day target. 50% productivity. Budget overrun imminent.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Programme Confidence', value: 61, unit: '%', severity: 'critical' } },
        { type: 'hud-metric', duration: 0, params: { id: 'budget-burn', kind: 'burndown', label: 'Budget', value: 11, target: 18, unit: '£M', severity: 'warning' } },
        panel('contractor', 'Contractor B — Performance', 'data', SITE_4, [
          { label: 'Productivity', value: '14m/day (target 28)', status: 'critical' },
          { label: 'Budget Burn', value: '£2.1M over forecast', status: 'critical' },
          { label: 'Root Cause', value: 'Resource shortage', status: 'critical' },
          { label: 'Days Behind', value: '22 days', status: 'critical' },
        ]),
      ]),

      beat(PACING.ORBIT, [
        orbit180(SITE_4, { startBearing: 30, arc: 120, zoom: 15.5, pitch: 55 }),
        hudEvent('ai', 'AI INTERVENTION', 'Re-prioritisation model running. Recommending resource rebalance from Scheme 5 to Scheme 4.', 'warning'),
        notification('AI RE-PRIORITISATION', 'Shifting Contractor B crew from Scheme 5 (mobilising) to Scheme 4 (critical).', 'warning', 5000),
      ]),

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(14, -16), infraId: 'depot-east', zoom: 15.5, pitch: 55, bearing: 100 } },
        updatePanel('contractor', [
          { label: 'Action', value: 'Resource Rebalance', status: 'warning' },
          { label: 'Crew Added', value: '+4 operatives', status: 'good' },
          { label: 'Revised Rate', value: '26m/day', status: 'warning' },
          { label: 'Recovery ETA', value: '8 days', status: 'warning' },
        ]),
        hudEvent('recovery', 'RECOVERY PLAN ACTIVE', 'Additional crew deployed. Revised productivity 26m/day. Recovery in 8 days.', 'success'),
      ]),

      removePanel('contractor'),

      productScreen('Contractor Performance — Analysis', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — contractor performance analytics TBD',
        role: 'Programme Manager',
        url: 'app.skewb.io/programme/contractor-performance',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: RECOVERY — Site 4 shifts amber, programme stabilises (100–135s)
    // ═══════════════════════════════════════════════════════════════════════
    act('Recovery', [
      { type: 'clear-effect', duration: 0, params: { id: 'risk-4' } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#f59e0b', emissive: 0.5 } } as ScenarioStep,
      { type: 'color-grade', duration: 0, params: { tint: '#f59e0b', intensity: 0.04, vignette: 0.12 } } as ScenarioStep,

      { type: 'clear-effect', duration: 0, params: { id: 'programme' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'programme',
        sites: [
          { coordinates: gp(-25, -12), status: 'green' },
          { coordinates: gp(18, 19), status: 'green' },
          { coordinates: gp(7, 25), status: 'green' },
          { coordinates: gp(14, -16), status: 'amber' },
          { coordinates: gp(-18, 28), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      beat(7000, [
        dronePushIn(SITE_4, { bearing: -20, zoom: 15.5, duration: 7000 }),
        { type: 'excavation', duration: 5000, params: { coordinates: gp(13, -16), radius: 12, id: 'exc-4a' } },
        { type: 'excavation', duration: 5000, params: { coordinates: gp(15, -16), radius: 12, id: 'exc-4b' } },
        hudEvent('progress', 'KINGS WAY — RECOVERING', 'Additional crew on site. Excavations commenced. 26m/day achieved.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'meterage', kind: 'counter', label: 'Meterage', value: 5100, unit: 'm / 12400m', severity: 'info' } },
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Programme Confidence', value: 74, unit: '%', severity: 'warning' } },
        highlightAt(gp(14, -16), 150),
      ]),

      beat(8000, [
        craneReveal(HUB, { zoom: 13.8, bearing: 0, duration: 8000 }),
        hudEvent('status', 'PROGRAMME STABILISED', 'All 5 schemes progressing. Zero RED. Budget reforecast accepted.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'budget-burn', kind: 'burndown', label: 'Budget', value: 12, target: 18, unit: '£M', severity: 'warning' } },
        hudTeam([
          { name: 'Contractor A (1-3)', status: 'on-track', destination: '2 complete, 1 active' },
          { name: 'Contractor B (4-5)', status: 'recovering', destination: 'Rebalanced' },
        ]),
        notification('PROGRAMME AMBER', 'Recovering. Budget reforecast locked. Target: all GREEN within 8 days.', 'warning', PACING.BEAT),
      ]),

      productScreen('Portfolio Delivery — Recovery', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — portfolio delivery with recovery tracking TBD',
        role: 'Programme Director',
        url: 'app.skewb.io/programme/portfolio',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 5: ALL GREEN — Programme RAG resolved (135–170s)
    // ═══════════════════════════════════════════════════════════════════════
    act('Programme Green', [
      { type: 'clear-effect', duration: 0, params: { id: 'programme' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'progress-2' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'pipe-2' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'pipe-3' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'exc-3a' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'exc-3b' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'exc-4a' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'exc-4b' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'meterage' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'budget-burn' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'confidence' } } as ScenarioStep,

      { type: 'multi-site-rag', duration: 0, params: {
        id: 'programme-green',
        sites: [
          { coordinates: gp(-25, -12), status: 'green' },
          { coordinates: gp(18, 19), status: 'green' },
          { coordinates: gp(7, 25), status: 'green' },
          { coordinates: gp(14, -16), status: 'green' },
          { coordinates: gp(-18, 28), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#10b981', emissive: 0.5 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#10b981', emissive: 0.5 } } as ScenarioStep,
      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.05, vignette: 0.1 } } as ScenarioStep,

      dimEnd,

      beat(8000, [
        craneReveal(HUB, { zoom: 13.5, bearing: -15, duration: 8000 }),
        hudEvent('complete', 'PROGRAMME ALL GREEN', '5 schemes on track. Zero RED. AI intervention saved 22 days and £1.8M.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'confidence', kind: 'counter', label: 'Programme Confidence', value: 92, unit: '%', severity: 'success' } },
        { type: 'hud-metric', duration: 0, params: { id: 'meterage', kind: 'counter', label: 'Meterage', value: 5800, unit: 'm / 12400m', severity: 'success' } },
        notification('ALL GREEN', '5 schemes — zero exceptions — programme confidence 92%.', 'success', 5000),
        hudRegion([
          { name: 'Scheme 1 — Station Rd', status: 'complete', properties: 45 },
          { name: 'Scheme 2 — Oak Lane', status: 'on-track', properties: 62 },
          { name: 'Scheme 3 — Park Rd', status: 'on-track', properties: 38 },
          { name: 'Scheme 4 — Kings Way', status: 'recovering', properties: 55 },
          { name: 'Scheme 5 — River Walk', status: 'on-track', properties: 40 },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'programme-green' } } as ScenarioStep,

      productScreen('Programme RAG — All Green', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — all-green programme RAG dashboard TBD',
        role: 'Programme Director',
        url: 'app.skewb.io/programme/rag',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.04, vignette: 0.1 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -20 }),
        notification('MAINS REHAB DELIVERED', '5 schemes — 12.4km programme — AI-driven recovery — all GREEN.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);

function highlightAt(coords: [number, number], radius = 120): ScenarioStep {
  return { type: 'highlight-buildings', duration: 0, params: { coordinates: coords, radius } };
}
